import { FC } from "react";
import { z } from "zod";
import { useSnackbar, VariantType } from "notistack";
import { useForm } from "@mantine/form";
import { Button, Grid, TextInput } from "@monolith-forensics/monolith-ui";

import { FormTitle, FormWrapper } from "./components/MonolithFormComponents";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";

import OfficeSelector from "./components/Selectors/OfficeSelector";
import ObserverSelector from "./components/Selectors/ObserverSelector";
import ButtonMenu from "./components/ButtonMenu";
import RoleSelector from "./components/Selectors/RoleSelector";
import { isLicenseAvailable } from "@/api";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import UserApi from "@/api/users/users";

interface CreateUserFormProps {
  width?: number | undefined;
  onSubmit: () => void; // Refetch query from parent component
  onCancel: () => void;
  buttonProps?: { cancelText?: string; submitText?: string };
}

const userValidationSchema = z.object({
  first_name: z
    .string({
      required_error: "First name is required",
    })
    .min(1, { message: "First name is required" }),
  last_name: z
    .string({
      required_error: "Last name is required",
    })
    .min(1, { message: "Last name is required" }),
  email: z
    .string({
      required_error: "Email is required",
    })
    .email({ message: "Invalid email" })
    .refine(
      async (email) => {
        const user = await UserApi.getUsers({ email: email });
        // returns false if user exists
        return !user[0];
      },
      { message: "Email already exists in the system" }
    ),
  password: z
    .preprocess(
      (val) => (val === "" || val === undefined ? undefined : val),
      z
        .string()
        .min(12, { message: "Must be 12 or more characters" })
        .refine(
          (password) => /[a-z]/.test(password),
          "Must contain lowercase letter"
        )
        .refine(
          (password) => /[A-Z]/.test(password),
          "Must contain uppercase letter"
        )
        .refine((password) => /[0-9]/.test(password), "Must contain number")
        .refine(
          (password) => /[!+_\-.@#$%^&*]/.test(password),
          "Must contain special character (!+_-.@#$%^&*)"
        )
        .optional()
    )
    .optional(),
  office_id: z.union([z.string(), z.number()], {
    required_error: "Office is required",
  }),
  role_id: z.union([z.string(), z.number()], {
    required_error: "Role is required",
  }),
  observer: z.union([z.string(), z.number()], {
    required_error: "Observer is required",
  }),
});

const CreateUserForm: FC<CreateUserFormProps> = ({
  width,
  onSubmit,
  onCancel,
  buttonProps,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const form = useForm({
    mode: "uncontrolled",
  });

  const handleSubmit = async () => {
    try {
      const formData = form.getValues();

      const validationResult = await validateAsync(userValidationSchema)(
        formData
      );
      // VALIDATION CHECKING

      // Check if form fields are valid according to Zod schema
      if (validationResult?.hasErrors) {
        console.error("Validation Errors", validationResult?.errors);
        form.setErrors(validationResult?.errors);
        return;
      }

      // Check if user limit has been reached
      if (!(await isLicenseAvailable())) {
        enqueueSnackbar(
          "User limit reached - deactivate a user or purchase more licenses.",
          {
            variant: "error",
          }
        );
        return;
      }

      // Prepare data for submission
      formData.active = 1;
      formData.username = formData.email.split("@")[0];
      formData.password = formData.password || null;

      await UserApi.createUser(formData);
      onSubmit?.();
      enqueueSnackbar(
        `User ${formData.first_name + " " + formData.last_name} created`,
        {
          variant: "success" as VariantType,
        }
      );
      // Log the creation of the user
      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `New User Created: Role: ${formData.user_role} | ${
          formData.first_name + " " + formData.last_name
        } | ${formData.username} | ${formData.email}`,
        event: "client:create",
        object_type: "client",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Create New User</FormTitle>
      <Grid col={2}>
        <TextInput
          size="sm"
          variant="outlined"
          label="First Name"
          required={true}
          placeholder="Enter user's first name"
          description="First name of the user"
          key={form.key("first_name")}
          {...form.getInputProps("first_name")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Last Name"
          required={true}
          placeholder="Enter user's last name"
          description="Last name of the user"
          key={form.key("last_name")}
          {...form.getInputProps("last_name")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Email"
          placeholder="Enter user's email"
          description="The email of the user"
          required={true}
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Password"
          placeholder="Leave blank to auto-generate"
          description="Set user's initial password"
          password={true}
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
        <OfficeSelector
          key={form.key("office_id")}
          {...form.getInputProps("office_id")}
        />
        <RoleSelector
          key={form.key("role_id")}
          {...form.getInputProps("role_id")}
        />
        <ObserverSelector
          key={form.key("observer")}
          {...form.getInputProps("observer")}
        />
      </Grid>
      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={handleCancel}>
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          {buttonProps?.submitText || "Create User"}
        </Button>
      </ButtonMenu>
    </FormWrapper>
  );
};

export default CreateUserForm;
